import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/Shell";
import { Plus, Users as UsersIcon, Pencil, Trash2, KeyRound, X } from "lucide-react";

const BLANK = { email:"", password:"", name:"", role:"engineer", phone:"", cluster_head_id:"", warehouse_id:"", warehouse_ids:[], active:true };

export default function Users() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [clusterHeads, setClusterHeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState("");
  const [form, setForm] = useState(BLANK);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");

  const load = async () => {
    const u = await api.get("/users");
    setItems(u.data);
    setClusterHeads(u.data.filter(x => x.role === "cluster_head" && x.active !== false));
    const w = await api.get("/warehouses");
    setWarehouses(w.data);
  };
  useEffect(() => { load().catch(e=>setErr(formatApiError(e))); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...form };
      if (payload.role !== "engineer") delete payload.cluster_head_id;
      if (payload.role !== "warehouse_manager") payload.warehouse_ids = [];
      // On edit, don't send an empty password
      if (editId && !payload.password) delete payload.password;

      if (editId) {
        await api.patch(`/users/${editId}`, payload);
      } else {
        if (!payload.password) { setErr("Password is required"); return; }
        await api.post("/users", payload);
      }
      setShowForm(false); setEditId(""); setForm(BLANK);
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  const startEdit = (u) => {
    setEditId(u.id);
    setForm({
      email: u.email || "",
      password: "",
      name: u.name || "",
      role: u.role || "engineer",
      phone: u.phone || "",
      cluster_head_id: u.cluster_head_id || "",
      warehouse_id: u.warehouse_id || "",
      warehouse_ids: u.warehouse_ids || [],
      active: u.active !== false,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = () => { setShowForm(false); setEditId(""); setForm(BLANK); setErr(""); };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate user "${u.name}"? They will no longer be able to sign in.`)) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const activate = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: true }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const resetPassword = async () => {
    if (!pwValue || pwValue.length < 6) { setErr("Password must be at least 6 characters"); return; }
    try {
      await api.patch(`/users/${pwUser.id}`, { password: pwValue });
      setPwUser(null); setPwValue("");
    } catch (e) { setErr(formatApiError(e)); }
  };

  const filtered = filter ? items.filter(i => i.role === filter) : items;

  return (
    <div className="p-4 md:p-8" data-testid="users-page">
      <PageHeader title="Users" subtitle="Admins, cluster heads, warehouse managers and field engineers."
        action={
          !showForm && (
            <button onClick={()=>{ setEditId(""); setForm(BLANK); setShowForm(true); }} data-testid="add-user-btn" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">
              <Plus size={14}/> Add User
            </button>
          )
        }
      />

      {showForm && (
        <Card className="p-5 mb-6" data-testid="user-form">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold">{editId ? "Edit User" : "Add User"}</div>
            <button onClick={cancel} className="text-zinc-500 hover:text-rose-600 inline-flex items-center gap-1 text-xs"><X size={14}/> Cancel</button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input required placeholder="Name" value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} className="inp" data-testid="user-name" />
            <input required type="email" placeholder="Email" value={form.email} onChange={e=>setForm(f=>({...f, email:e.target.value}))} className="inp" data-testid="user-email" disabled={!!editId} title={editId ? "Email cannot be changed" : ""} />
            <input type="password" placeholder={editId ? "New password (leave blank to keep)" : "Password"} value={form.password} onChange={e=>setForm(f=>({...f, password:e.target.value}))} className="inp" data-testid="user-password" />
            <select value={form.role} onChange={e=>setForm(f=>({...f, role:e.target.value}))} className="inp" data-testid="user-role">
              <option value="engineer">Engineer</option>
              <option value="cluster_head">Cluster Head</option>
              <option value="warehouse_manager">Warehouse Manager</option>
              <option value="admin">Admin</option>
            </select>
            <input placeholder="Phone" value={form.phone} onChange={e=>setForm(f=>({...f, phone:e.target.value}))} className="inp" data-testid="user-phone" />
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={e=>setForm(f=>({...f, active:e.target.checked}))} data-testid="user-active" /> Active
            </label>
            {form.role === "engineer" && (
              <select value={form.cluster_head_id} onChange={e=>setForm(f=>({...f, cluster_head_id:e.target.value}))} className="inp md:col-span-3" data-testid="user-cluster">
                <option value="">Map to Cluster Head</option>
                {clusterHeads.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>
            )}
            {form.role === "warehouse_manager" && (
              <div className="md:col-span-3" data-testid="user-wh-multiselect">
                <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-700 mb-1">Warehouses managed</div>
                {warehouses.length === 0 ? (
                  <div className="text-xs text-zinc-500 border border-zinc-200 rounded-md p-3">No warehouses yet. Create warehouses first.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border border-zinc-200 rounded-md p-3">
                    {warehouses.map(w => (
                      <label key={w.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.warehouse_ids.includes(w.id)}
                          onChange={e=>{
                            const ids = new Set(form.warehouse_ids);
                            if (e.target.checked) ids.add(w.id); else ids.delete(w.id);
                            setForm(f=>({...f, warehouse_ids: Array.from(ids)}));
                          }}
                          data-testid={`user-wh-${w.name.replace(/\s+/g,'-').toLowerCase()}`}
                        />
                        {w.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            {err && <div className="md:col-span-3 text-sm text-rose-600">{err}</div>}
            <button type="submit" data-testid="user-save-btn" className="md:col-span-3 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">{editId ? "Update User" : "Create User"}</button>
          </form>
        </Card>
      )}

      {pwUser && (
        <Card className="p-5 mb-6 border-[#F97316]" data-testid="reset-pw-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">Reset password — {pwUser.name}</div>
            <button onClick={()=>{setPwUser(null); setPwValue("");}} className="text-zinc-500 hover:text-rose-600"><X size={14}/></button>
          </div>
          <div className="flex gap-2">
            <input type="password" placeholder="New password (min 6 chars)" value={pwValue} onChange={e=>setPwValue(e.target.value)} className="inp flex-1" data-testid="reset-pw-input" />
            <button onClick={resetPassword} data-testid="reset-pw-btn" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">Reset</button>
          </div>
          {err && <div className="text-xs text-rose-600 mt-2">{err}</div>}
        </Card>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {["", "admin", "cluster_head", "warehouse_manager", "engineer"].map(r => (
          <button key={r} onClick={()=>setFilter(r)} data-testid={`user-filter-${r||"all"}`} className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${filter===r ? "bg-[#F97316] text-white border-[#F97316]" : "bg-white text-zinc-600 border-zinc-200"}`}>
            {(r || "all").replace("_"," ").toUpperCase()}
          </button>
        ))}
      </div>

      {err && !showForm && !pwUser && <div className="text-rose-600 text-sm mb-3">{err}</div>}
      {filtered.length === 0 ? <EmptyState title="No users yet" desc="Click 'Add User' to create your first team member." icon={UsersIcon} /> :
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-left">Mapping</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} className={`border-t border-zinc-100 hover:bg-zinc-50 ${u.active === false ? "opacity-60" : ""}`}>
                    <td className="px-4 py-3 font-semibold">{u.name}</td>
                    <td className="px-4 py-3 num text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-zinc-100 text-zinc-700">{u.role.replace("_"," ")}</span>
                    </td>
                    <td className="px-4 py-3 num text-xs">{u.phone || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {u.role === "engineer" && (u.cluster_head_name ? `→ ${u.cluster_head_name}` : "—")}
                      {u.role === "warehouse_manager" && `${(u.warehouse_ids||[]).length} warehouse(s)`}
                    </td>
                    <td className="px-4 py-3">
                      {u.active === false
                        ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-rose-50 text-rose-600 border border-rose-200">Inactive</span>
                        : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={()=>startEdit(u)} data-testid={`edit-user-${u.email}`} className="text-xs font-semibold text-[#F97316] hover:underline inline-flex items-center gap-1 mr-3"><Pencil size={12}/> Edit</button>
                      <button onClick={()=>{setPwUser(u); setPwValue(""); setErr("");}} data-testid={`reset-user-${u.email}`} className="text-xs font-semibold text-zinc-700 hover:underline inline-flex items-center gap-1 mr-3"><KeyRound size={12}/> Reset PW</button>
                      {u.active !== false ? (
                        <button onClick={()=>deactivate(u)} data-testid={`deactivate-user-${u.email}`} className="text-xs font-semibold text-rose-600 hover:underline inline-flex items-center gap-1"><Trash2 size={12}/> Deactivate</button>
                      ) : (
                        <button onClick={()=>activate(u)} data-testid={`activate-user-${u.email}`} className="text-xs font-semibold text-emerald-700 hover:underline">Activate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      }
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent } .inp:disabled { background:#F4F4F5; color:#71717A }`}</style>
    </div>
  );
}
